import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  CloudSun, Sun, Cloud, CloudRain, CloudDrizzle, CloudLightning, Snowflake,
  Users, DollarSign, TrendingUp,
} from "lucide-react";

/**
 * PublicPredictionsRow
 * --------------------
 * Read-only view of whichever predictions the organizer opted to share
 * with vendors. Shown on the event detail page. Renders nothing if
 * nothing is shared — keeps the page tight when the organizer wants
 * their numbers private.
 */
const ICONS = {
  sun: Sun, "cloud-sun": CloudSun, cloud: Cloud,
  "cloud-rain": CloudRain, "cloud-drizzle": CloudDrizzle,
  "cloud-lightning": CloudLightning, snowflake: Snowflake,
};

const money = (n) => `$${Math.round(n).toLocaleString()}`;
const num = (n) => Number(n || 0).toLocaleString();

export default function PublicPredictionsRow({ eventId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventId) return;
    api.get(`/events/${eventId}/predictions`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [eventId]);

  if (loading || !data) return null;
  const { weather, attendance, revenue } = data;
  if (!weather && !attendance && !revenue) return null;

  return (
    <div className="card-warm rounded-sm p-5 sm:p-6 mt-6"
         data-testid="public-predictions-row">
      <div className="overline inline-flex items-center gap-1.5"
           style={{ color: "var(--moss)" }}>
        <TrendingUp className="h-3 w-3" /> What to expect
      </div>
      <div className="mt-3 grid sm:grid-cols-3 gap-4">
        {weather && !weather.unavailable && (
          <PredictionTile
            testId="public-predictions-weather"
            Icon={ICONS[weather.icon] || CloudSun}
            label="Weather"
            headline={`${weather.high_f}° / ${weather.low_f}°F`}
            sub={`${weather.label} · ${weather.precip_pct}% rain`}
          />
        )}
        {weather && weather.unavailable && (
          <PredictionTile
            testId="public-predictions-weather-pending"
            Icon={CloudSun}
            label="Weather"
            headline="Coming closer to the date"
            sub={weather.reason || "Forecast lands within 16 days of the event."}
            muted
          />
        )}
        {attendance && (
          <PredictionTile
            testId="public-predictions-attendance"
            Icon={Users}
            label="Expected crowd"
            headline={`${num(attendance.min)}–${num(attendance.max)}`}
            sub="Estimate — actual varies with weather + marketing"
          />
        )}
        {revenue && (
          <PredictionTile
            testId="public-predictions-revenue"
            Icon={DollarSign}
            label="Est. spend / vendor"
            headline={`${money(revenue.per_vendor_min)}–${money(revenue.per_vendor_max)}`}
            sub="Rough range based on category avg — yours may vary a lot"
          />
        )}
      </div>
    </div>
  );
}

function PredictionTile({ Icon, label, headline, sub, testId, muted }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <Icon className="h-6 w-6 mt-1 shrink-0"
            style={{ color: muted ? "var(--ink-soft)" : "var(--moss)" }} />
      <div className="min-w-0">
        <div className="overline" style={{ color: "var(--ink-soft)" }}>{label}</div>
        <div className="font-serif text-xl mt-0.5 break-words"
             style={{ color: muted ? "var(--ink-soft)" : "var(--ink)" }}>
          {headline}
        </div>
        <div className="text-[11px]" style={{ color: "var(--ink-soft)" }}>{sub}</div>
      </div>
    </div>
  );
}
